<template>
  <div class="content">
    <h3 ref="pageHeader" class="pageHeader">实训室可视化数据统计</h3>

    <!-- ===== 一、顶部全局KPI区域 ===== -->
    <div class="kpi-row">
      <div class="kpi-card" v-for="(item, idx) in kpiList" :key="idx" v-loading="kpiLoading">
        <div class="kpi-info">
          <div class="kpi-title">{{ item.name }}</div>
          <div class="kpi-value">{{ item.value }}</div>
          <div class="kpi-sub">{{ item.sub }}</div>
        </div>
        <div class="kpi-detail-btn" @click="handleKpiDetail(item.path)">详</div>
        <div class="kpi-detail-shade"></div>
      </div>
    </div>

    <!-- ===== 二、上半区图表网格 2行×3列 ===== -->
    <div class="chart-grid">
      <!-- 第1行左：院系实训室数量统计 -->
      <div class="chart-card" v-loading="roomCountLoading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-s-data"></i> 院系实训室数量统计</span>
            <div class="card-subtitle">展示各院系的实训室数量</div>
          </div>
        </div>
        <div class="card-body">
          <v-chart auto-resize v-if="roomCountOption.yAxis && roomCountOption.yAxis.data && roomCountOption.yAxis.data.length"
           :options="roomCountOption" class="chart-box" />
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>

      <!-- 实训室风险等级分布 -->
      <div class="chart-card" v-loading="riskPieLoading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-pie-chart"></i> 实训室风险等级分布</span>
            <div class="card-subtitle">展示全校各个分级实训室比例</div>
          </div>
        </div>
        <div class="card-body">
          <v-chart auto-resize v-if="riskPieOption.series && riskPieOption.series[0].data && riskPieOption.series[0].data.length"
           :options="riskPieOption" class="chart-box" />
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>

      <!-- 实训室分级分类分布 -->
      <div class="chart-card" v-loading="matrixLoading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-menu"></i> 实训室分级分类分布</span>
            <div class="card-subtitle">展示实训室分级分类数量</div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="classMatrix.rows.length && classMatrix.cols.length" class="matrix-table-wrapper">
            <table class="matrix-table">
              <thead>
                <tr>
                  <th>级别 / 分类</th>
                  <th v-for="col in classMatrix.cols" :key="col">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in classMatrix.rows" :key="ri">
                  <td class="row-label">{{ row }}</td>
                  <td v-for="(col, ci) in classMatrix.cols" :key="ci">
                    <span class="matrix-cell"
                      :style="{ background: getCellColor(classMatrix.data[ri] && classMatrix.data[ri][ci] || 0), color: getCellTextColor(classMatrix.data[ri] && classMatrix.data[ri][ci] || 0) }">
                      {{ (classMatrix.data[ri] && classMatrix.data[ri][ci]) || 0 }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>

      <!-- 实训室隐患数量TOP5 -->
      <div class="chart-card" v-loading="hazardTop5Loading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-warning"></i> 实训室隐患数量TOP5</span>
            <div class="card-subtitle">展示实训室未整改安全隐患数量排行</div>
          </div>
        </div>
        <div class="card-body">
          <v-chart auto-resize v-if="hazardTop5Option.yAxis && hazardTop5Option.yAxis.data && hazardTop5Option.yAxis.data.length"
           :options="hazardTop5Option" class="chart-box" />
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>

      <!-- 实训室安全检查次数统计 -->
      <div class="chart-card" v-loading="activityLoading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-circle-check"></i> 实训室安全检查次数统计</span>
            <div class="card-subtitle">多维度统计各实训室安全检查次数</div>
          </div>
          <el-radio-group v-model="activityDateRange" size="mini" @change="fetchActivityCount">
            <el-radio-button label="近半年">近半年</el-radio-button>
            <el-radio-button label="近一年">近一年</el-radio-button>
          </el-radio-group>
        </div>
        <div class="card-body">
          <v-chart auto-resize v-if="activityOption.yAxis && activityOption.yAxis.data && activityOption.yAxis.data.length"
           :options="activityOption" class="chart-box" />
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>

      <!-- 近半年资源出入库金额变动 -->
      <div class="chart-card" v-loading="inoutLineLoading">
        <div class="card-header">
          <div>
            <span class="card-title"><i class="el-icon-s-marketing"></i> 近半年资源出入库金额变动</span>
            <div class="card-subtitle">展示仪器设备、耗材、化学品出入库趋势</div>
          </div>
          <el-radio-group v-model="deviceStatType" size="mini" @change="fetchInoutLine">
            <el-radio-button label="1">仪器设备</el-radio-button>
            <el-radio-button label="2">耗材</el-radio-button>
            <el-radio-button label="3">化学品</el-radio-button>
          </el-radio-group>
        </div>
        <div class="card-body">
          <v-chart auto-resize v-if="inoutLineOption.xAxis && inoutLineOption.xAxis.data && inoutLineOption.xAxis.data.length"
           :options="inoutLineOption" class="chart-box" />
          <el-empty v-else description="暂无数据" :image-size="80" />
        </div>
      </div>
    </div>

    <!-- ===== 三、下半区单实训室详情板块 ===== -->
    <div class="detail-section">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <div>
          <span class="section-label"><i class="el-icon-search"></i> 实训室资源数据分析</span>
          <div class="section-subtitle">实训室仪器设备、耗材、化学品数据统计</div>
        </div>
        <div class="search-controls">
          <el-autocomplete v-model="searchValue" :fetch-suggestions="searchRooms" placeholder="请输入实训室名称搜索"
            :debounce="300" @select="handleRoomSelect" clearable style="width: 360px;" prefix-icon="el-icon-search">
            <template v-slot="{ item }">
              <div class="search-item">{{ item.value }}</div>
            </template>
          </el-autocomplete>
        </div>
      </div>

      <!-- 实训室详情信息 -->
      <div class="resource-card" v-if="roomDetail">
        <div class="resource-header">
          <span class="resource-title"><i class="el-icon-info"></i> 实训室信息-{{ selectedRoom.name || roomDetail.training_name || '未知' }}</span>
        </div>
        <div class="resource-body">
          <div class="room-info-grid">
        <div class="room-info-item"><span class="info-label">安全负责人</span><span class="info-value">{{ roomDetail.secure_person || '-' }}</span></div>
        <div class="room-info-item"><span class="info-label">待整改隐患</span><span class="info-value">{{ roomDetail.hazard_count || '0' }}</span></div>
        <div class="room-info-item"><span class="info-label">化学品总数</span><span class="info-value">{{ roomDetail.chemical_count || '0' }}</span></div>
        <div class="room-info-item"><span class="info-label">负责人联系方式</span><span class="info-value">{{ roomDetail.secure_phone || '-' }}</span></div>
        <div class="room-info-item"><span class="info-label">实训室分级</span><span class="info-value">{{ roomDetail.security_level || '-' }}级</span></div>
        <div class="room-info-item"><span class="info-label">仪器设备总数</span><span class="info-value">{{ roomDetail.device_count || '0' }}</span></div>
        <div class="room-info-item"><span class="info-label">目前状态</span><span class="info-value">{{ roomDetail.state || '-' }}</span></div>
        <div class="room-info-item"><span class="info-label">实训室分类</span><span class="info-value">{{ roomDetail.cate || '-' }}</span></div>
        <div class="room-info-item"><span class="info-label">耗材总数</span><span class="info-value">{{ roomDetail.consumed_product_count || '0' }}</span></div>
        <div class="room-info-item"><span class="info-label">专职人员数量</span><span class="info-value">{{ roomDetail.relation_user || '0' }}</span></div>
        <div class="room-info-item"><span class="info-label">近半年安全巡查次数</span><span class="info-value">{{ roomDetail.check_count || '0' }}次</span></div>
        <div class="room-info-item"><span class="info-label">近半年安全自查次数</span><span class="info-value">{{ roomDetail.self_check_count || '0' }}次</span></div>
      </div>
        </div>
      </div>

      <!-- 三大资源子模块 -->
      <div class="resource-modules" v-if="selectedRoom">
        <!-- 仪器设备 -->
        <div class="resource-card">
          <div class="resource-header">
            <span class="resource-title"><i class="el-icon-s-platform"></i> 仪器设备</span>
          </div>
          <div class="resource-body" v-loading="resourceModules.device.loading">
            <div class="resource-grid">
              <!-- 明细表格 -->
              <div class="resource-table-wrap">
                <div class="sub-title">库存明细</div>
                <el-table :data="resourceModules.device.tableData" size="small" max-height="280" stripe border
                  v-if="resourceModules.device.tableData.length">
                  <el-table-column v-for="col in resourceModules.device.columns" :key="col.prop" :prop="col.prop"
                    :label="col.label" :width="col.width" show-overflow-tooltip />
                </el-table>
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <!-- 出入库折线趋势 -->
              <div class="resource-chart-wrap">
                <div class="sub-title">出入库金额</div>
                <v-chart auto-resize v-if="resourceModules.device.lineOption.xAxis && resourceModules.device.lineOption.xAxis.data && resourceModules.device.lineOption.xAxis.data.length"
                 :options="resourceModules.device.lineOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <!-- 出库排行 -->
              <div class="resource-chart-wrap">
                <div class="sub-title">出库排行</div>
                <v-chart auto-resize v-if="resourceModules.device.barOption.yAxis && resourceModules.device.barOption.yAxis.data && resourceModules.device.barOption.yAxis.data.length"
                 :options="resourceModules.device.barOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
            </div>
          </div>
        </div>

        <!-- 低值易耗品 -->
        <div class="resource-card">
          <div class="resource-header">
            <span class="resource-title"><i class="el-icon-s-goods"></i> 低值易耗品</span>
          </div>
          <div class="resource-body" v-loading="resourceModules.consumed.loading">
            <div class="resource-grid">
              <div class="resource-table-wrap">
                <div class="sub-title">库存明细</div>
                <el-table :data="resourceModules.consumed.tableData" size="small" max-height="280" stripe border
                  v-if="resourceModules.consumed.tableData.length">
                  <el-table-column v-for="col in resourceModules.consumed.columns" :key="col.prop" :prop="col.prop"
                    :label="col.label" :width="col.width" show-overflow-tooltip />
                </el-table>
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <div class="resource-chart-wrap">
                <div class="sub-title">出入库金额</div>
                <v-chart auto-resize v-if="resourceModules.consumed.lineOption.xAxis && resourceModules.consumed.lineOption.xAxis.data && resourceModules.consumed.lineOption.xAxis.data.length"
                 :options="resourceModules.consumed.lineOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <div class="resource-chart-wrap">
                <div class="sub-title">出库排行</div>
                <v-chart auto-resize v-if="resourceModules.consumed.barOption.yAxis && resourceModules.consumed.barOption.yAxis.data && resourceModules.consumed.barOption.yAxis.data.length"
                 :options="resourceModules.consumed.barOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
            </div>
          </div>
        </div>

        <!-- 化学品 -->
        <div class="resource-card">
          <div class="resource-header">
            <span class="resource-title"><i class="el-icon-s-opportunity"></i> 化学品</span>
          </div>
          <div class="resource-body" v-loading="resourceModules.chemical.loading">
            <div class="resource-grid">
              <div class="resource-table-wrap">
                <div class="sub-title">库存明细</div>
                <el-table :data="resourceModules.chemical.tableData" size="small" max-height="280" stripe border
                  v-if="resourceModules.chemical.tableData.length">
                  <el-table-column v-for="col in resourceModules.chemical.columns" :key="col.prop" :prop="col.prop"
                    :label="col.label" :width="col.width" show-overflow-tooltip />
                </el-table>
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <div class="resource-chart-wrap">
                <div class="sub-title">出入库金额</div>
                <v-chart auto-resize v-if="resourceModules.chemical.lineOption.xAxis && resourceModules.chemical.lineOption.xAxis.data && resourceModules.chemical.lineOption.xAxis.data.length"
                 :options="resourceModules.chemical.lineOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
              <div class="resource-chart-wrap">
                <div class="sub-title">出库排行</div>
                <v-chart auto-resize v-if="resourceModules.chemical.barOption.yAxis && resourceModules.chemical.barOption.yAxis.data && resourceModules.chemical.barOption.yAxis.data.length"
                 :options="resourceModules.chemical.barOption" class="mini-chart" />
                <el-empty v-else description="暂无数据" :image-size="60" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 未选择实训室时的占位提示 -->
      <div v-else class="no-room-placeholder">
        <el-empty description="请在搜索框中搜索并选择一个实训室查看详情" :image-size="100" />
      </div>
    </div>
  </div>
</template>

<script>
import ECharts from 'vue-echarts/components/ECharts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/dataZoom';

// ===== 全局配色常量 =====
const COLORS = {
  primary: '#4578ed',
  primaryLight: '#7ba3f5',
  warning: '#e6a23c',
  warningLight: '#f5d78f',
  danger: '#f56c6c',
  success: '#67c23a',
  gray: '#909399',
  riskLevels: ['#f56c6c', '#e6a23c', '#67c23a', '#909399']
};

// ===== 通用横向柱状图模板 =====
function createHorizontalBarOption(colorStart, colorEnd) {
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    grid: { left: '3%', right: '14%', top: '5%', bottom: '12%', containLabel: true },
    xAxis: {
      type: 'value',
      name: '数量',
      nameLocation: 'middle',
      nameGap: 24,
      axisLabel: { color: '#6b7280' }
    },
    yAxis: {
      type: 'category',
      data: [],
      inverse: true,
      axisLabel: { color: '#1f2937', fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false }
    },
    series: [{
      name: '数量',
      type: 'bar',
      data: [],
      barWidth: 18,
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 1, y2: 0,
          colorStops: [
            { offset: 0, color: colorStart },
            { offset: 1, color: colorEnd }
          ]
        },
        barBorderRadius: [0, 4, 4, 0]
      },
      label: { show: true, position: 'right', color: '#1f2937', fontSize: 12 }
    }],
    dataZoom: [
      { type: 'inside', yAxisIndex: 0, start: 0, end: 100, minSpan: 20 }
    ]
  };
}

// ===== 通用折线图模板 =====
function createLineOption() {
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['入库金额', '出库金额'], top: 0, textStyle: { color: '#6b7280', fontSize: 12 } },
    grid: { left: '3%', right: '5%', top: '15%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: [], boundaryGap: false, axisLabel: { color: '#6b7280' } },
    yAxis: { type: 'value', name: '金额(元)', axisLabel: { color: '#6b7280' } },
    series: [
      {
        name: '入库金额', type: 'line', smooth: true, data: [],
        lineStyle: { color: '#4578ed', width: 2 },
        itemStyle: { color: '#4578ed' },
        areaStyle: { color: 'rgba(69,120,237,0.08)' }
      },
      {
        name: '出库金额', type: 'line', smooth: true, data: [],
        lineStyle: { color: '#e6a23c', width: 2, type: 'dashed' },
        itemStyle: { color: '#e6a23c' }
      }
    ]
  };
}

// ===== 通用排行柱状图模板 =====
function createRankBarOption() {
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '12%', top: '5%', bottom: '5%', containLabel: true },
    xAxis: { type: 'value', axisLabel: { color: '#6b7280' } },
    yAxis: {
      type: 'category', data: [], inverse: true,
      axisLabel: { color: '#1f2937', fontSize: 10 },
      axisLine: { show: false }, axisTick: { show: false }
    },
    series: [{
      type: 'bar', data: [], barWidth: 12,
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 1, y2: 0,
          colorStops: [
            { offset: 0, color: '#4578ed' },
            { offset: 1, color: '#7ba3f5' }
          ]
        },
        barBorderRadius: [0, 3, 3, 0]
      },
      label: { show: true, position: 'right', color: '#1f2937', fontSize: 11 }
    }]
  };
}

export default {
  name: 'dataStatistics',
  components: { 'v-chart': ECharts },
  data() {
    return {
      // ===== KPI 指标 =====
      kpiList: [],
      kpiLoading: false,

      // ===== 上半区图表 =====
      roomCountOption: createHorizontalBarOption('#4578ed', '#7ba3f5'),
      roomCountLoading: false,

      riskPieOption: {
        // 饼图角度仍由 level_rate 驱动，tooltip 上额外展示 level_count（该等级实训室数量）
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const count = params.data && params.data.count !== undefined ? params.data.count : '-';
            return `${params.name}: ${count} (${params.percent}%)<br/>`;
          }
        },
        legend: { orient: 'vertical', right: '5%', top: 'center', textStyle: { color: '#6b7280', fontSize: 12 } },
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['35%', '50%'],
          data: [],
          label: { show: true, formatter: '{b}\n{d}%', fontSize: 11, color: '#6b7280' },
          emphasis: {
            label: { fontSize: 14, fontWeight: 'bold' },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.15)' }
          },
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 }
        }],
        color: COLORS.riskLevels
      },
      riskPieLoading: false,

      classMatrix: { rows: [], cols: [], data: [] },
      matrixLoading: false,

      hazardTop5Option: createHorizontalBarOption('#e6a23c', '#f5d78f'),
      hazardTop5Loading: false,

      activityOption: createHorizontalBarOption('#4578ed', '#7ba3f5'),
      activityDateRange: '近一年',
      activityLoading: false,

      inoutLineOption: createLineOption(),
      inoutLineLoading: false,
      deviceStatType: '1',

      // ===== 下半区详情 =====
      selectedRoom: null,
      roomDetail: null,
      searchValue: '',
      searchLoading: false,

      // 三大资源模块
      resourceModules: {
        device:    { tableData: [], columns: [{ prop: 'device_name', label: '设备名称' }, { prop: 'device_qty', label: '设备数量' }, { prop: 'total_price', label: '设备金额' }], lineOption: createLineOption(), barOption: createRankBarOption(), loading: false },
        consumed:  { tableData: [], columns: [{ prop: 'name', label: '耗材名称' }, { prop: 'quantity', label: '耗材数量' }, { prop: 'total_price', label: '耗材金额' }], lineOption: createLineOption(), barOption: createRankBarOption(), loading: false },
        chemical:  { tableData: [], columns: [{ prop: 'name', label: '化学品名称' }, { prop: 'quantity', label: '化学品数量' }, { prop: 'total_price', label: '化学品金额' }], lineOption: createLineOption(), barOption: createRankBarOption(), loading: false }
      },
    };
  },
  mounted() {
    this.fetchKpi();
    this.fetchRoomCount();
    this.fetchRiskPie();
    this.fetchClassMatrix();
    this.fetchHazardTop5();
    this.fetchActivityCount();
    this.fetchInoutLine();
    this.handleRoomSelect({id:0})
  },
  methods: {
    // ===== KPI指标 =====
    async fetchKpi() {
      this.kpiLoading = true;
      try {
        const res = await this.$api.instance.interfaceServer('api/cockpit/statistics', 'get');
        if (res.code === 0 && res.data) {
          const d = res.data;
          // 接口返回 {roomCount, deviceCount, chemicalCount, consumeCount, hazardCount, ...}
          const kpiDefs = [
            { name: '实训室总数', value: d.roomCount || 0, sub: `同比增量: ${d.compareRoomCount}`, path: '/trainingRoom' },
            { name: '仪器设备总数', value: d.deviceCount || 0, sub: d.deviceTotalPrice ? `库存金额: ¥${Number(d.deviceTotalPrice).toLocaleString()}` : '', path: '/equipmentInventory' },
            { name: '化学品库存总数', value: d.chemicalCount || 0, sub: d.chemicalTotalPrice ? `库存金额: ¥${Number(d.chemicalTotalPrice).toLocaleString()}` : '', path: '/chemicalInventory' },
            { name: '耗材库存总数', value: d.consumeCount || 0, sub: d.consumeTotalPrice ? `库存金额: ¥${Number(d.consumeTotalPrice).toLocaleString()}` : '', path: '/consumableSurplusByStructure' },
            { name: '待整改隐患', value: (d.hazardCount || 0) + (d.overdueHazardCount || 0), sub: `逾期: ${d.overdueHazardCount || 0}项`, path: '/hazardRectification' }
          ];
          this.kpiList = kpiDefs;
        }
      } catch (e) {
        console.error('获取KPI数据失败:', e);
      } finally {
        this.kpiLoading = false;
      }
    },

    // ===== 院系实训室数量统计 =====
    async fetchRoomCount() {
      this.roomCountLoading = true;
      try {
        const res = await this.$api.instance.interfaceServer('api/cockpit/trainingRoomCount', 'get');
        if (res.code === 0 && res.data) {
          const arr = Array.isArray(res.data) ? res.data : (res.data.list || []);
          // 接口返回 [{structureName, structureRoomCount}]
          arr.sort((a, b) => (b.structureRoomCount || 0) - (a.structureRoomCount || 0));
          this.roomCountOption.yAxis.data = arr.map(d => d.structureName || d.name || '');
          this.roomCountOption.series[0].data = arr.map(d => d.structureRoomCount || d.count || 0);
          this.roomCountOption.series[0].name = '实训室数量';
        }
      } catch (e) {
        console.error('获取院系实训室数据失败:', e);
      } finally {
        this.roomCountLoading = false;
      }
    },

    // ===== 风险等级饼图 =====
    async fetchRiskPie() {
      this.riskPieLoading = true;
      try {
        const res = await this.$api.instance.interfaceServer('api/cockpit/trainingRoomSafeLevelCount', 'get');
        if (res.code === 0 && res.data) {
          const arr = Array.isArray(res.data) ? res.data : (res.data.list || []);
          // 接口返回 [{level_name, level_rate, level_count}], level_rate为百分比字符串如"0%", level_count为实训室数量
          this.riskPieOption.series[0].data = arr.map(d => ({
            name: d.level_name || d.name || '',
            value: parseFloat(d.level_rate) || 0,
            // count 不参与饼图角度计算，仅供 tooltip 展示数量
            count: Number(d.level_count) || 0
          }));
        }
      } catch (e) {
        console.error('获取风险等级数据失败:', e);
      } finally {
        this.riskPieLoading = false;
      }
    },

    // ===== 分级分类矩阵 =====
    async fetchClassMatrix() {
      this.matrixLoading = true;
      try {
        const res = await this.$api.instance.interfaceServer('api/cockpit/trainingRoomClassCount', 'get');
        if (res.code === 0 && res.data && Array.isArray(res.data) && res.data.length) {
          // 接口返回 [{class1_level_name, class1_level_count, ..., class5_level_name, class5_level_count}, ...]
          // 4个风险等级 × 5个分类，level_name格式如 "Ⅰ级/红色级实验室 - 生物类"
          const rows = [];           // 风险等级名称
          const cols = [];           // 分类名称
          const matrixData = [];     // 二维数组 [row][col]

          res.data.forEach((row, ri) => {
            const countData = [];
            for (let i = 1; i <= 5; i++) {
              const nameKey = `class${i}_level_name`;
              const countKey = `class${i}_level_count`;
              if (row[nameKey]) {
                // 从 "Ⅰ级/红色级实验室 - 生物类" 中提取级别和分类
                const parts = row[nameKey].split(' - ');
                if (i === 1) {
                  // 每行的第一个 class 提取行标签（级别名），只在第一行提取列标签
                  rows.push(parts[0] || row[nameKey]);
                  if (ri === 0) {
                    cols.push(parts[1] || `分类${i}`);
                  }
                } else if (ri === 0) {
                  // 第一行的其余 class 提取列标签
                  cols.push(parts[1] || `分类${i}`);
                }
                countData.push(Number(row[countKey]) || 0);
              }
            }
            matrixData.push(countData);
          });

          this.classMatrix.rows = rows;
          this.classMatrix.cols = cols;
          this.classMatrix.data = matrixData;
        }
      } catch (e) {
        console.error('获取分级分类数据失败:', e);
      } finally {
        this.matrixLoading = false;
      }
    },

    // ===== 矩阵单元格颜色（按数值区间分级） =====
    // 0-2: #1FA061  2-4: #C38F15  4-6: #C55C3F  6以上: #C7354B
    getCellColor(val) {
      if (val <= 2) return '#1FA061';
      if (val <= 4) return '#C38F15';
      if (val <= 6) return '#C55C3F';
      return '#C7354B';
    },
    getCellTextColor(val) {
      return '#ffffff';
    },

    // ===== 隐患TOP5 =====
    async fetchHazardTop5() {
      this.hazardTop5Loading = true;
      try {
        const res = await this.$api.instance.interfaceServer('api/cockpit/trainingRoomCheckCount', 'get');
        if (res.code === 0 && res.data && Array.isArray(res.data)) {
          // 接口返回 [{target_id, hazard_count, target_name}]
          const arr = [...res.data].sort((a, b) => (b.hazard_count || 0) - (a.hazard_count || 0));
          const top5 = arr.slice(0, 5);
          this.hazardTop5Option.yAxis.data = top5.map(d => d.target_name || '未命名实训室');
          this.hazardTop5Option.series[0].data = top5.map(d => d.hazard_count || 0);
          this.hazardTop5Option.series[0].name = '隐患数量';
          this.hazardTop5Option.xAxis.name = '隐患数量';
        }
      } catch (e) {
        console.error('获取隐患TOP5数据失败:', e);
      } finally {
        this.hazardTop5Loading = false;
      }
    },

    // ===== 安全检查次数（支持时间切换） =====
    async fetchActivityCount() {
      this.activityLoading = true;
      try {
        const dateStr = this.activityDateRange || '近一年';
        const res = await this.$api.instance.interfaceServer(
          `api/cockpit/trainingRoomActivityCount?dateStr=${encodeURIComponent(dateStr)}`, 'get'
        );
        if (res.code === 0 && res.data) {
          const arr = Array.isArray(res.data) ? res.data : (res.data.list || []);
          // 接口返回 [{target_id, target_name, hazard_count}]
          arr.sort((a, b) => (b.hazard_count || 0) - (a.hazard_count || 0));
          this.activityOption.yAxis.data = arr.map(d => d.target_name || '未命名实训室');
          this.activityOption.series[0].data = arr.map(d => d.hazard_count || 0);
          this.activityOption.series[0].name = '检查次数';
          this.activityOption.xAxis.name = '次数';
        }
      } catch (e) {
        console.error('获取安全检查次数失败:', e);
      } finally {
        this.activityLoading = false;
      }
    },

    // ===== 出入库金额折线 =====
    async fetchInoutLine() {
      this.inoutLineLoading = true;
      try {
        const res = await this.$api.instance.interfaceServer(
          `api/cockpit/deviceStatistics?type=${this.deviceStatType}`, 'get'
        );
        if (res.code === 0 && res.data && Array.isArray(res.data)) {
          // 接口返回 [{month, xxx_storage_amount, xxx_scrap_amount}, ...]
          // 按月聚合（同一个月可能有多条数据）
          const monthMap = {};
          res.data.forEach(d => {
            const m = d.month || '';
            if (!m) return;
            if (!monthMap[m]) {
              monthMap[m] = { inAmount: 0, outAmount: 0 };
            }
            Object.keys(d).forEach(k => {
              if (k.endsWith('_storage_amount')) monthMap[m].inAmount += Number(d[k] || 0);
              if (k.endsWith('_scrap_amount')) monthMap[m].outAmount += Number(d[k] || 0);
            });
          });
          const months = Object.keys(monthMap).sort();
          this.inoutLineOption.xAxis.data = months;
          this.inoutLineOption.series[0].data = months.map(m => monthMap[m].inAmount);
          this.inoutLineOption.series[1].data = months.map(m => monthMap[m].outAmount);
          this.inoutLineOption.legend.data = ['入库金额', '出库金额'];
        }
      } catch (e) {
        console.error('获取出入库金额数据失败:', e);
      } finally {
        this.inoutLineLoading = false;
      }
    },

    // ===== 搜索实训室 =====
    async searchRooms(query, cb) {
      if (!query || query.trim().length === 0) {
        cb([]);
        return;
      }
      try {
        const res = await this.$api.instance.interfaceServer(
          `api/cockpit/trainingRoomSearch?name=${encodeURIComponent(query)}`, 'get'
        );
        if (res.code === 0 && res.data) {
          // 接口返回 [{id, training_name}]
          const arr = Array.isArray(res.data) ? res.data : (res.data.list || []);
          const suggestions = arr.map(d => ({
            value: d.training_name || d.name || '',
            id: d.id || ''
          }));
          cb(suggestions);
        } else {
          cb([]);
        }
      } catch (e) {
        console.error('搜索实训室失败:', e);
        cb([]);
      }
    },

    // ===== 选中实训室 =====
    async handleRoomSelect(item) {
      // if (!item || !item.id) return;
      this.selectedRoom = { id: item.id, name: item.value };
      this.searchValue = item.value;
      // 加载实训室详情
      this.fetchRoomDetail(item.id);
      // 加载三大资源模块
      this.fetchResourceModule('device', item.id);
      this.fetchResourceModule('consumed', item.id);
      this.fetchResourceModule('chemical', item.id);
    },

    // ===== 实训室详情 =====
    async fetchRoomDetail(roomId) {
      try {
        const res = await this.$api.instance.interfaceServer(`api/cockpit/trainingRoomDetail?id=${roomId}`, 'get');
        if (res.code === 0 && res.data) {
          this.roomDetail = res.data;
        }
      } catch (e) {
        console.error('获取实训室详情失败:', e);
      }
    },

    // ===== KPI详情跳转 =====
    handleKpiDetail(path) {
      if (path) this.$router.push(path);
    },

    // ===== 清除选中 =====
    clearSelectedRoom() {
      this.selectedRoom = null;
      this.searchValue = '';
      // 清空资源数据
      ['device', 'consumed', 'chemical'].forEach(type => {
        const m = this.resourceModules[type];
        m.tableData = [];
        m.lineOption = createLineOption();
        m.barOption = createRankBarOption();
      });
    },

    // ===== 加载资源模块数据 =====
    async fetchResourceModule(type, roomId) {
      const mod = this.resourceModules[type];
      mod.loading = true;
      const apiMap = {
        device: 'api/cockpit/trainingRoomDevice',
        consumed: 'api/cockpit/trainingRoomConsumed',
        chemical: 'api/cockpit/trainingRoomChemical'
      };
      try {
        const res = await this.$api.instance.interfaceServer(`${apiMap[type]}?id=${roomId}`, 'get');
        if (res.code === 0 && res.data) {
          const data = res.data;
          // 接口返回 {list: [{name, quantity, total_price}], line: [{month, xxx_storage_amount, xxx_scrap_amount}], bar: [{name, quantity}]}

          // 明细表格
          const tableData = data.list || [];
          mod.tableData = Array.isArray(tableData) ? tableData : [];

          // 折线趋势 — 按月聚合，字段名以 _storage_amount / _scrap_amount 结尾
          const lineArr = data.line || [];
          if (lineArr.length) {
            const monthMap = {};
            lineArr.forEach(d => {
              const m = d.month;
              if (!m) return;
              if (!monthMap[m]) monthMap[m] = { inAmount: 0, outAmount: 0 };
              // 自动匹配字段：xxx_storage_amount → 入库, xxx_scrap_amount → 出库
              Object.keys(d).forEach(k => {
                if (k.endsWith('_storage_amount')) monthMap[m].inAmount += Number(d[k] || 0);
                if (k.endsWith('_scrap_amount')) monthMap[m].outAmount += Number(d[k] || 0);
              });
            });
            const months = Object.keys(monthMap).sort();
            mod.lineOption = createLineOption();
            mod.lineOption.xAxis.data = months;
            mod.lineOption.series[0].data = months.map(m => monthMap[m].inAmount);
            mod.lineOption.series[1].data = months.map(m => monthMap[m].outAmount);
          }

          // 出库排行
          const barArr = data.bar || [];
          if (barArr.length) {
            mod.barOption = createRankBarOption();
            mod.barOption.yAxis.data = barArr.map(d => d.name || d.device_name || '');
            mod.barOption.series[0].data = barArr.map(d => d.quantity || d.device_qty || 0);
            mod.barOption.series[0].name = '出库量';
            mod.barOption.xAxis.name = '出库量';
          }
        }
      } catch (e) {
        console.error(`获取${type}资源数据失败:`, e);
      } finally {
        mod.loading = false;
      }
    }
  }
};
</script>
<style lang="less" scoped>
// ===== 全局基础样式 =====
.content {
  background: #f7f8fa;
  padding: 16px;
  min-height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
}


// ===== 顶部KPI栏 =====
.kpi-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  .kpi-card {
    flex: 1 1 0;
    min-width: 140px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e8edf5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 16px 20px;
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }

    .kpi-info {
      .kpi-title {
        font-size: 14px;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 4px;
      }

      .kpi-value {
        font-size: 28px;
        font-weight: 700;
        color: #1f2937;
        line-height: 1.2;
      }

      .kpi-sub {
        font-size: 12px;
        color: #9ca3af;
        margin-top: 2px;
      }
    }

    .kpi-detail-btn {
      position: absolute;
      top: 0;
      right: 0;
      width: 28px;
      height: 28px;
      background: linear-gradient(135deg, #5b8cf0, #4578ed);
      color: #ffffff;
      font-size: 13px;
      font-weight: 600;
      border-radius: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 2;
      box-shadow: 0 2px 6px rgba(69, 120, 237, 0.4);
      transition: all 0.3s;

      &:hover {
        background: linear-gradient(135deg, #4a7de0, #3568d5);
        box-shadow: 0 3px 8px rgba(69, 120, 237, 0.55);
        transform: translateY(-1px);
      }
    }

    .kpi-detail-shade {
      position: absolute;
      top: 0;
      right: 0;
      width: 56px;
      height: 56px;
      background: rgba(69, 120, 237, 0.12);
      border-radius: 0 8px 0 50%;
      transition: background 0.3s;
      pointer-events: none;
    }

    .kpi-detail-btn:hover + .kpi-detail-shade {
      background: rgba(230, 162, 60, 0.25);
    }
  }
}

// ===== 图表网格 =====
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8edf5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-shrink: 0;
    min-width: 0;

    .card-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;

      i {
        color: #4578ed;
        margin-right: 4px;
      }
    }

    .card-subtitle {
      font-size: 12px;
      color: #9ca3af;
      margin-top: 2px;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }
  }

  .card-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .chart-box {
    width: 100%;
    height: 320px;
    overflow: hidden;
  }
}

// ===== 分级分类矩阵表格 =====
.matrix-table-wrapper {
  overflow-x: auto;
  max-height: 320px;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border: 1px solid #e8edf5;
    padding: 12px 8px;
    text-align: center;
  }

  th {
    background: #ffffff;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
  }

  .row-label {
    background: #ffffff;
    color: #1f2937;
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
  }

  td {
    font-weight: 500;
    min-width: 50px;
  }

  .matrix-cell {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 4px;
    min-width: 52px;
    text-align: center;
    font-weight: 500;
    width:100%;
    box-sizing: border-box;
  }
}

// ===== 下半区详情板块 =====

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px 20px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8edf5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .section-label {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    white-space: nowrap;

    i {
      color: #4578ed;
      margin-right: 4px;
    }
  }

  .section-subtitle {
    font-size: 12px;
    color: #9ca3af;
  }

  .search-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

}

.search-item {
  padding: 4px 0;
  font-size: 14px;
}

.no-room-placeholder {
  padding: 40px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8edf5;
}

// ===== 实训室详情信息 =====
.room-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #e8edf5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;

  .room-info-item {
    background: #ffffff;
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .info-label {
      font-size: 13px;
      color: #6b7280;
      white-space: nowrap;
    }

    .info-value {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      text-align: right;
    }
  }
}

// ===== 资源模块 =====
.resource-modules {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.resource-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8edf5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.resource-header {
  padding: 16px 20px 0;

  .resource-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;

    i {
      color: #4578ed;
      margin-right: 4px;
    }
  }
}

.resource-body {
  padding: 12px 20px 20px;
  min-height: 200px;
}

.resource-grid {
  display: flex;
  gap: 16px;
  min-height: 320px;

  .resource-table-wrap {
    flex: 1.3;
    min-width: 0;
    overflow: auto;
    max-width: 100%;

    /deep/.el-table {
      min-width: 100%;
    }
  }

  .resource-chart-wrap {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .resource-chart-wrap:last-child {
    flex: 0.9;
  }

  .sub-title {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
    font-weight: bold;
  }

  .mini-chart {
    width: 100%;
    height: 280px;
  }
}

// ===== 响应式布局 =====
@media (max-width: 1400px) {
  .resource-modules .resource-grid {
    gap: 12px;

    .resource-table-wrap { flex: 1.2; }
    .resource-chart-wrap:last-child { flex: 1; }
  }
}

@media (max-width: 1200px) {
  .resource-modules .resource-grid {
    flex-wrap: wrap;
    gap: 12px;

    .resource-table-wrap { flex: 1 1 calc(50% - 6px); }
    .resource-chart-wrap { flex: 1 1 calc(50% - 6px); }
    .resource-chart-wrap:last-child { flex: 1 1 100%; }
  }
}

@media (max-width: 992px) {
  .kpi-row {
    flex-wrap: wrap;

    .kpi-card {
      flex: 1 1 calc(33.33% - 12px);
    }
  }
}

@media (max-width: 768px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .kpi-row {
    .kpi-card {
      flex: 1 1 calc(50% - 8px);
      min-width: 120px;
      padding: 12px;

      .kpi-value {
        font-size: 22px;
      }
    }
  }

  .search-bar {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;

    .el-autocomplete {
      width: 100% !important;
    }
  }

  .resource-card {
    .resource-header {
      padding: 12px 14px 0;
    }

    .resource-body {
      padding: 10px 14px 14px;
    }
  }

  .room-info-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .resource-modules .resource-grid {
    flex-direction: column;
    gap: 12px;

    .resource-table-wrap,
    .resource-chart-wrap {
      flex: 1 1 auto;
    }
  }

  .mini-chart {
    height: 240px !important;
  }

  .chart-box {
    height: 260px !important;
  }

  .matrix-table {
    font-size: 12px;

    th, td {
      padding: 6px 8px;
    }
  }
}

@media (max-width: 576px) {
  .room-info-grid {
    grid-template-columns: 1fr;
  }

  .kpi-row {
    gap: 8px;

    .kpi-card {
      flex: 1 1 calc(50% - 4px);
      padding: 10px;

      .kpi-value {
        font-size: 20px;
      }

      .kpi-title {
        font-size: 12px;
      }
    }
  }

  .chart-card {
    padding: 14px;
  }

  .chart-box {
    height: 240px !important;
  }

  .mini-chart {
    height: 220px !important;
  }

  .search-bar {
    padding: 10px 12px;

    .section-label {
      font-size: 14px;
    }
  }

  .resource-card {
    .resource-header {
      padding: 10px 12px 0;

      .resource-title {
        font-size: 14px;
      }
    }

    .resource-body {
      padding: 8px 12px 12px;
    }
  }
}
</style>
